import { useEffect, useState, useCallback } from "react";
import { Loader2, Wand2, X, RefreshCw, BookCheck } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "../../lib/api";
import KiBuchungssatzResult from "./KiBuchungssatzResult";

/**
 * Jun-2026 (User-Wunsch) — 1-Klick „Buchungssatz vorschlagen" direkt aus einem
 * Beleg (Kreditor/EC). Ruft beim Öffnen automatisch den KI-Vorschlag auf und
 * erlaubt Anpassung von USt-Satz & Kontenrahmen mit erneutem Vorschlag.
 *
 * @param {object} prefill - { buchungstext, betrag_brutto, ust_satz, richtung,
 *                             kontenrahmen, geschaeftspartner, zahlungsart,
 *                             source_type, source_id }
 */
export default function KiBuchungssatzModal({ prefill, onClose, onBooked }) {
    const [ustSatz, setUstSatz] = useState(prefill?.ust_satz ?? 19);
    const [kontenrahmen, setKontenrahmen] = useState(prefill?.kontenrahmen || "SKR03");
    const [loading, setLoading] = useState(false);
    const [result, setResult] = useState(null);
    const [booking, setBooking] = useState(false);
    const isEc = prefill?.source_type === "ec-beleg";
    const canBook = (prefill?.source_type === "kreditor" && prefill?.source_id) || isEc;

    const run = useCallback(async (ust, kr) => {
        setLoading(true);
        setResult(null);
        try {
            const r = await api.post("/buchhaltung/ki-buchungssatz", {
                buchungstext: prefill.buchungstext,
                betrag_brutto: Number(prefill.betrag_brutto),
                ust_satz: Number(ust),
                richtung: prefill.richtung || "ausgabe",
                kontenrahmen: kr,
                geschaeftspartner: prefill.geschaeftspartner || null,
                zahlungsart: prefill.zahlungsart || "offen",
                source_type: prefill.source_type || null,
                source_id: prefill.source_id || null,
            });
            setResult(r.data);
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "KI-Vorschlag fehlgeschlagen");
        } finally {
            setLoading(false);
        }
    }, [prefill]);

    const uebernehmen = async () => {
        if (!result || !canBook) return;
        setBooking(true);
        try {
            const satz = {
                soll_konto: result.soll_konto,
                soll_konto_name: result.soll_konto_name,
                haben_konto: result.haben_konto,
                haben_konto_name: result.haben_konto_name,
                steuerschluessel: result.steuerschluessel || null,
                steuer_hinweis: result.steuer_hinweis || null,
                kontenrahmen: result.kontenrahmen,
                betrag_brutto: result.betrag_brutto,
                betrag_netto: result.betrag_netto,
                ust_satz: result.ust_satz,
                ust_betrag: result.ust_betrag,
                begruendung: result.begruendung || null,
            };
            if (isEc) {
                await api.post(`/buchhaltung/ec/buchungssatz`, {
                    ...satz,
                    merchant: prefill.geschaeftspartner || null,
                    invoice_number: prefill.source_id || null,
                    beleg_datum: prefill.beleg_datum || null,
                    card_last4: prefill.card_last4 || null,
                    buchungstext: prefill.buchungstext || null,
                });
            } else {
                await api.post(`/buchhaltung/kreditoren/${prefill.source_id}/buchungssatz`, satz);
            }
            toast.success("Buchungssatz übernommen — fließt in den DATEV-Export ein");
            onBooked?.();
            onClose?.();
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Übernehmen fehlgeschlagen");
        } finally {
            setBooking(false);
        }
    };

    useEffect(() => { run(ustSatz, kontenrahmen); /* auto beim Öffnen */ // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-start justify-center p-4 overflow-y-auto" data-testid="ki-beleg-modal">
            <div className="bg-gray-50 rounded-sm w-full max-w-2xl mt-8 border border-gray-200" onClick={(e) => e.stopPropagation()}>
                <div className="flex items-center gap-2 px-5 py-3 border-b border-gray-200 bg-white">
                    <Wand2 className="w-5 h-5 text-[#111827]" />
                    <h3 className="font-display font-black text-base">Buchungssatz vorschlagen</h3>
                    <button onClick={onClose} className="ml-auto text-gray-400 hover:text-gray-700" data-testid="ki-beleg-close"><X className="w-5 h-5" /></button>
                </div>

                <div className="p-5 space-y-4">
                    <div className="bg-white border border-gray-200 rounded-sm p-3 text-sm">
                        <div className="text-[10px] font-bold uppercase tracking-wider text-gray-400">Beleg</div>
                        <div className="font-bold text-[#111827]">{prefill?.buchungstext}</div>
                        <div className="text-xs text-gray-500 mt-0.5">
                            {prefill?.geschaeftspartner ? `${prefill.geschaeftspartner} · ` : ""}
                            {(Number(prefill?.betrag_brutto) || 0).toLocaleString("de-DE", { minimumFractionDigits: 2 })} € brutto
                        </div>
                    </div>

                    <div className="flex items-center gap-3">
                        <div>
                            <label className="text-[10px] font-bold uppercase tracking-wider text-gray-500">USt-Satz</label>
                            <select value={ustSatz} onChange={(e) => { setUstSatz(e.target.value); run(e.target.value, kontenrahmen); }} className="block h-9 rounded-sm border border-gray-300 px-2 text-sm mt-1" data-testid="ki-beleg-ust">
                                <option value={19}>19 %</option>
                                <option value={7}>7 %</option>
                                <option value={0}>0 %</option>
                            </select>
                        </div>
                        <div>
                            <label className="text-[10px] font-bold uppercase tracking-wider text-gray-500">Kontenrahmen</label>
                            <select value={kontenrahmen} onChange={(e) => { setKontenrahmen(e.target.value); run(ustSatz, e.target.value); }} className="block h-9 rounded-sm border border-gray-300 px-2 text-sm mt-1" data-testid="ki-beleg-kr">
                                <option value="SKR03">SKR03</option>
                                <option value="SKR04">SKR04</option>
                            </select>
                        </div>
                        <button onClick={() => run(ustSatz, kontenrahmen)} disabled={loading} className="ml-auto self-end h-9 inline-flex items-center gap-1 text-xs font-bold uppercase tracking-wider text-gray-600 hover:text-[#111827] border border-gray-300 rounded-sm px-3 bg-white" data-testid="ki-beleg-rerun">
                            <RefreshCw className="w-3.5 h-3.5" /> Neu
                        </button>
                    </div>

                    {loading ? (
                        <div className="flex items-center gap-2 text-gray-500 py-10 justify-center"><Loader2 className="w-5 h-5 animate-spin" /> KI denkt nach…</div>
                    ) : (
                        <>
                            <KiBuchungssatzResult result={result} />
                            {result && canBook && (
                                <button type="button" onClick={uebernehmen} disabled={booking}
                                    className="w-full h-12 rounded-sm bg-emerald-600 hover:bg-emerald-700 text-white font-black uppercase tracking-wider text-sm inline-flex items-center justify-center gap-2 disabled:opacity-60"
                                    data-testid="ki-beleg-uebernehmen">
                                    {booking ? <Loader2 className="w-5 h-5 animate-spin" /> : <><BookCheck className="w-5 h-5" /> Buchung übernehmen</>}
                                </button>
                            )}
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}
